import { useEffect, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ResetAuditLedgerStrip } from "@/components/dashboard/ResetAuditLedgerStrip";
import { MerchantLogoUploader } from "@/components/dashboard/MerchantLogoUploader";
import { SubscriptionLifecycleGrid } from "@/components/dashboard/SubscriptionLifecycleGrid";
import { CURSOR_COLORS, COLOR_LS_KEY, INTENSITY_COUNTS, INTENSITY_LS_KEY, PRISM_SPEEDS, PRISM_SPEED_LS_KEY, PRISM_ENGINE_ACTIVE_KEY, METEOR_INTENSITIES, METEOR_INTENSITY_LS_KEY } from "@/components/dashboard/SpaceAgeControlDeck";

// ============================================================================
// Profile · account preferences.
//   1. Master `🛸 Activate Space-Age Control Deck Interface` toggle (persists
//      `space_age_deck_enabled`).
//   2. Dedicated `🛸 Space-Age Control Deck Interface Options` card wrapper
//      holding density slider + weather thresholds + cursor mode, with a
//      single master `💾 Save Configuration Changes` button that flushes all
//      dirty state in one PATCH round-trip.
// ============================================================================

const clampDensity = (n) => Math.max(2, Math.min(12, Number(n) || 6));

export default function Profile() {
  const { user, checkAuth } = useAuth();
  const [saving, setSaving] = useState(false);
  const enabled = !!user?.space_age_deck_enabled;

  // Draft state — flushed only when the user hits Save Configuration.
  const [density, setDensity] = useState(clampDensity(user?.space_age_deck_density));
  const [cursor, setCursor] = useState(!!user?.space_age_cursor_enabled);
  const [cursorColor, setCursorColor] = useState(() => {
    try { const k = localStorage.getItem(COLOR_LS_KEY); return CURSOR_COLORS[k] ? k : "cyan"; } catch { return "cyan"; }
  });
  const [cursorIntensity, setCursorIntensity] = useState(() => {
    try { const k = localStorage.getItem(INTENSITY_LS_KEY); return INTENSITY_COUNTS[k] !== undefined ? k : "medium"; } catch { return "medium"; }
  });
  const [wxMin, setWxMin] = useState(user?.space_age_weather_min ?? "");
  const [wxMax, setWxMax] = useState(user?.space_age_weather_max ?? "");
  const [cfgSaving, setCfgSaving] = useState(false);

  // Prism speed — localStorage-only (no server round-trip needed).
  const [prismSpeed, setPrismSpeed] = useState(() => {
    try {
      const k = localStorage.getItem(PRISM_SPEED_LS_KEY);
      return PRISM_SPEEDS[k] !== undefined ? k : "normal";
    } catch { return "normal"; }
  });

  // Meteor shower intensity — localStorage-only, syncs to CosmicWeatherCanvas via CustomEvent.
  const [meteorIntensity, setMeteorIntensity] = useState(() => {
    try {
      const k = localStorage.getItem(METEOR_INTENSITY_LS_KEY);
      return METEOR_INTENSITIES[k] !== undefined ? k : "normal";
    } catch { return "normal"; }
  });

  // Engine active — master on/off gate for MicroStarsCanvas + SheetLightningOverlay.
  const [engineActive, setEngineActive] = useState(() => {
    try {
      const v = localStorage.getItem(PRISM_ENGINE_ACTIVE_KEY);
      return v === null ? true : v !== "false";
    } catch { return true; }
  });

  useEffect(() => {
    setDensity(clampDensity(user?.space_age_deck_density));
    setCursor(!!user?.space_age_cursor_enabled);
    setWxMin(user?.space_age_weather_min ?? "");
    setWxMax(user?.space_age_weather_max ?? "");
  }, [
    user?.space_age_deck_density,
    user?.space_age_cursor_enabled,
    user?.space_age_weather_min,
    user?.space_age_weather_max,
  ]);

  const dirty =
    clampDensity(user?.space_age_deck_density) !== density
    || !!user?.space_age_cursor_enabled !== cursor
    || (String(user?.space_age_weather_min ?? "") !== String(wxMin))
    || (String(user?.space_age_weather_max ?? "") !== String(wxMax));

  const toggleDeck = async () => {
    if (saving) return;
    const next = !enabled;
    setSaving(true);
    const toastId = toast.loading(next ? "Powering up Control Deck…" : "Stowing Control Deck…");
    try {
      await api.patch("/auth/space-age-deck", { enabled: next });
      await checkAuth();
      toast.success(
        next
          ? "🛸 Space-Age Control Deck online · commodity feed + weather live"
          : "Ledger workspace restored · minimalist mode",
        { id: toastId },
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Toggle failed", { id: toastId });
    } finally {
      setSaving(false);
    }
  };

  const handleCursorColor = (key) => {
    try { localStorage.setItem(COLOR_LS_KEY, key); } catch { /* ignore */ }
    setCursorColor(key);
    window.dispatchEvent(new CustomEvent("rs:cursor:color-change", { detail: { color: key } }));
  };

  const handlePrismSpeed = (key) => {
    try { localStorage.setItem(PRISM_SPEED_LS_KEY, key); } catch { /* ignore */ }
    setPrismSpeed(key);
    window.dispatchEvent(
      new CustomEvent("rs:prism:speed-change", {
        detail: { speed: key, multiplier: PRISM_SPEEDS[key] },
      })
    );
  };

  const handleMeteorIntensity = (key) => {
    try { localStorage.setItem(METEOR_INTENSITY_LS_KEY, key); } catch { /* ignore */ }
    setMeteorIntensity(key);
    window.dispatchEvent(
      new CustomEvent("rs:meteor:intensity-change", {
        detail: { intensity: key, cfg: METEOR_INTENSITIES[key] },
      })
    );
  };

  const handleEngineToggle = () => {
    const next = !engineActive;
    try { localStorage.setItem(PRISM_ENGINE_ACTIVE_KEY, String(next)); } catch { /* ignore */ }
    setEngineActive(next);
    window.dispatchEvent(
      new CustomEvent("rs:engine:toggle", { detail: { active: next } })
    );
  };

  const handleCursorIntensity = (key) => {
    try { localStorage.setItem(INTENSITY_LS_KEY, key); } catch { /* ignore */ }
    setCursorIntensity(key);
    window.dispatchEvent(new CustomEvent("rs:cursor:intensity-change", { detail: { intensity: key } }));
  };

  const saveConfig = async () => {    if (cfgSaving) return;
    setCfgSaving(true);
    const toastId = toast.loading("Saving deck configuration…");
    const send = {
      density: clampDensity(density),
      cursor_enabled: !!cursor,
    };
    // The backend rejects null for the float thresholds — omit them entirely
    // when the field is cleared so the persisted value is left alone.
    if (wxMin !== "" && wxMin != null && !Number.isNaN(Number(wxMin))) send.weather_min = Number(wxMin);
    if (wxMax !== "" && wxMax != null && !Number.isNaN(Number(wxMax))) send.weather_max = Number(wxMax);
    try {
      await api.patch("/auth/space-age-prefs", send);
      await checkAuth();
      toast.success("💾 Configuration saved", { id: toastId });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed", { id: toastId });
    } finally {
      setCfgSaving(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-2xl mx-auto space-y-6 py-8">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.28em] text-cyan-300">
            Preferences · Control Deck
          </div>
          <h1 className="text-2xl font-bold text-white mt-1">Profile Settings</h1>
          <p className="text-sm text-slate-400 mt-1">
            {user?.email || "—"}
            {user?.is_admin && (
              <span
                className="ml-2 text-[9px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-md border border-cyan-400/50 text-cyan-200"
                data-testid="profile-admin-badge"
              >
                Admin
              </span>
            )}
          </p>
        </div>

        {/* Master toggle stays outside the config card so users can flip the
            deck on / off without touching any downstream setting. */}
        <div
          className="rounded-2xl p-5 border"
          style={{
            borderColor: enabled ? "rgba(6,182,212,0.65)" : "rgba(148,163,184,0.25)",
            background: "linear-gradient(180deg, rgba(6,25,55,0.85), rgba(15,23,42,0.9))",
            boxShadow: enabled ? "0 0 22px rgba(6,182,212,0.35)" : undefined,
            backdropFilter: "blur(16px)",
            WebkitBackdropFilter: "blur(16px)",
          }}
          data-testid="profile-space-age-card"
        >
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <div className="text-[10px] font-mono uppercase tracking-[0.28em] text-cyan-300 mb-1">
                🛸 Activate Space-Age Control Deck Interface
              </div>
              <p className="text-sm text-slate-200 leading-relaxed">
                Master switch. Paints the industrial commodity ticker footer and Field Dispatch
                Weather HUD across the dashboard. Turning this off completely hides both
                surfaces and restores the minimalist ledger workspace.
              </p>
            </div>
            <button
              type="button"
              onClick={toggleDeck}
              disabled={saving}
              role="switch"
              aria-checked={enabled}
              aria-label="Toggle Space-Age Control Deck"
              data-testid="profile-space-age-toggle"
              className="relative shrink-0 w-14 h-8 rounded-full transition-colors disabled:opacity-50"
              style={{
                background: enabled ? "rgba(6,182,212,0.28)" : "rgba(148,163,184,0.18)",
                border: `1px solid ${enabled ? "rgba(6,182,212,0.75)" : "rgba(148,163,184,0.35)"}`,
                boxShadow: enabled ? "0 0 14px rgba(6,182,212,0.55)" : undefined,
              }}
            >
              <span
                className="absolute top-0.5 h-6 w-6 rounded-full transition-all"
                style={{
                  left: enabled ? "26px" : "3px",
                  background: enabled
                    ? "radial-gradient(circle at 30% 30%, #7dd3fc, #06B6D4)"
                    : "radial-gradient(circle at 30% 30%, #cbd5e1, #64748b)",
                  boxShadow: enabled ? "0 0 8px #06B6D4" : "0 0 4px rgba(148,163,184,0.5)",
                }}
              />
            </button>
          </div>
          <div
            className="mt-4 text-[10px] font-mono uppercase tracking-widest"
            style={{ color: enabled ? "#67e8f9" : "#64748b" }}
            data-testid="profile-space-age-status"
          >
            {saving ? "· syncing…" : enabled ? "● deck online" : "○ deck offline"}
          </div>
        </div>

        {/* Dedicated deck options card — density slider + weather thresholds
            + cursor toggle, with one master Save Configuration Changes button
            at the base. */}
        {enabled && (
          <div
            className="rounded-2xl p-5 border border-cyan-500/40"
            style={{
              background: "linear-gradient(180deg, rgba(6,25,55,0.85), rgba(15,23,42,0.9))",
              backdropFilter: "blur(16px)",
              WebkitBackdropFilter: "blur(16px)",
            }}
            data-testid="profile-deck-config-card"
          >
            <div className="text-[10px] font-mono uppercase tracking-[0.28em] text-cyan-300 mb-3">
              🛸 Space-Age Control Deck Interface Options
            </div>

            {/* Density slider */}
            <div className="pb-4 border-b border-cyan-500/20" data-testid="profile-density-block">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] font-mono uppercase tracking-widest text-slate-300">
                  📊 Deck Density · Commodity Feed
                </span>
                <span
                  className="text-lg font-bold text-cyan-200 tabular-nums"
                  style={{ textShadow: "0 0 10px rgba(6,182,212,0.7)" }}
                  data-testid="profile-density-value"
                >
                  {density}
                </span>
              </div>
              <input
                type="range"
                min={2}
                max={12}
                step={1}
                value={density}
                onChange={(e) => setDensity(Number(e.target.value))}
                data-testid="profile-density-slider"
                className="w-full h-1 appearance-none rounded-full outline-none"
                style={{
                  background: `linear-gradient(90deg, #06B6D4 ${((density - 2) / 10) * 100}%, rgba(148,163,184,0.15) ${((density - 2) / 10) * 100}%)`,
                  accentColor: "#06B6D4",
                }}
              />
              <div className="flex justify-between mt-1 text-[8px] font-mono tracking-widest text-slate-500">
                <span>02 · minimal</span>
                <span>06 · standard</span>
                <span>12 · dense</span>
              </div>
            </div>

            {/* Weather thresholds */}
            <div className="py-4 border-b border-cyan-500/20" data-testid="profile-weather-block">
              <div className="text-[10px] font-mono uppercase tracking-widest text-slate-300 mb-2">
                🎯 Weather Dispatch Thresholds
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  inputMode="numeric"
                  placeholder="min °F"
                  value={wxMin ?? ""}
                  onChange={(e) => setWxMin(e.target.value)}
                  data-testid="profile-weather-min-input"
                  className="w-28 h-9 px-2 text-sm font-mono rounded-md outline-none"
                  style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(148,163,184,0.2)", color: "#e2e8f0" }}
                />
                <span className="text-slate-500">↔</span>
                <input
                  type="number"
                  inputMode="numeric"
                  placeholder="max °F"
                  value={wxMax ?? ""}
                  onChange={(e) => setWxMax(e.target.value)}
                  data-testid="profile-weather-max-input"
                  className="w-28 h-9 px-2 text-sm font-mono rounded-md outline-none"
                  style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(148,163,184,0.2)", color: "#e2e8f0" }}
                />
                <span className="text-[9px] font-mono uppercase tracking-widest text-slate-500">
                  crimson toast fires outside this band
                </span>
              </div>
            </div>

            {/* Cursor toggle */}
            <div className="py-4" data-testid="profile-cursor-block">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-slate-300 mb-1">
                    🛸 Space Age Cursor Mode
                  </div>
                  <p className="text-xs text-slate-400 leading-relaxed">
                    Hides the browser cursor and paints a cyber-ring cursor with a neon core
                    that grows on interactive hover.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setCursor((v) => !v)}
                  role="switch"
                  aria-checked={cursor}
                  data-testid="profile-cursor-toggle"
                  className="relative shrink-0 w-14 h-8 rounded-full transition-colors"
                  style={{
                    background: cursor ? "rgba(6,182,212,0.28)" : "rgba(148,163,184,0.18)",
                    border: `1px solid ${cursor ? "rgba(6,182,212,0.75)" : "rgba(148,163,184,0.35)"}`,
                    boxShadow: cursor ? "0 0 14px rgba(6,182,212,0.55)" : undefined,
                  }}
                >
                  <span
                    className="absolute top-0.5 h-6 w-6 rounded-full transition-all"
                    style={{
                      left: cursor ? "26px" : "3px",
                      background: cursor
                        ? "radial-gradient(circle at 30% 30%, #7dd3fc, #06B6D4)"
                        : "radial-gradient(circle at 30% 30%, #cbd5e1, #64748b)",
                      boxShadow: cursor ? "0 0 8px #06B6D4" : "0 0 4px rgba(148,163,184,0.5)",
                    }}
                  />
                </button>
              </div>

              {/* Aura color picker — visible only when cursor mode is on */}
              {cursor && (
                <div className="mt-4" data-testid="profile-cursor-color-picker">
                  <div className="text-[9px] font-mono uppercase tracking-[0.22em] text-slate-500 mb-2">
                    Aura ring color
                  </div>
                  <div className="flex items-center gap-2">
                    {Object.entries(CURSOR_COLORS).map(([key, c]) => (
                      <button
                        key={key}
                        type="button"
                        onClick={() => handleCursorColor(key)}
                        data-testid={`profile-cursor-color-${key}`}
                        aria-label={`${key} aura`}
                        className="w-8 h-8 rounded-full transition-all"
                        style={{
                          background: `radial-gradient(circle at 35% 35%, rgba(${c.r},${c.g},${c.b},0.9), rgba(${c.r},${c.g},${c.b},0.55))`,
                          border: cursorColor === key
                            ? `2px solid rgba(${c.r},${c.g},${c.b},1)`
                            : `1px solid rgba(${c.r},${c.g},${c.b},0.35)`,
                          boxShadow: cursorColor === key
                            ? `0 0 14px rgba(${c.r},${c.g},${c.b},0.75), inset 0 0 6px rgba(${c.r},${c.g},${c.b},0.4)`
                            : undefined,
                          transform: cursorColor === key ? "scale(1.18)" : "scale(1)",
                        }}
                      />
                    ))}
                    <span className="text-[9px] font-mono uppercase tracking-widest text-slate-500 ml-1">
                      {cursorColor} · active
                    </span>
                  </div>

                  {/* Trail intensity pills */}
                  <div className="text-[9px] font-mono uppercase tracking-[0.22em] text-slate-500 mb-2 mt-4">
                    Comet trail density
                  </div>
                  <div className="flex items-center gap-2" data-testid="profile-cursor-intensity-picker">
                    {["light", "medium", "intense"].map((key) => (
                      <button
                        key={key}
                        type="button"
                        onClick={() => handleCursorIntensity(key)}
                        data-testid={`profile-cursor-intensity-${key}`}
                        className="px-3 h-7 rounded-full text-[10px] font-mono uppercase tracking-widest transition-all"
                        style={{
                          background: cursorIntensity === key ? "rgba(6,182,212,0.18)" : "rgba(255,255,255,0.04)",
                          border: cursorIntensity === key ? "1px solid rgba(6,182,212,0.7)" : "1px solid rgba(255,255,255,0.08)",
                          color: cursorIntensity === key ? "#67e8f9" : "#64748b",
                          boxShadow: cursorIntensity === key ? "0 0 10px rgba(6,182,212,0.35)" : undefined,
                        }}
                      >
                        {key}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* ── SPEC 1702: Celestial Prism Speed Overdrive ──────────────── */}
            <div className="py-4 border-t border-white/5" data-testid="profile-prism-speed-block">
              <div className="flex items-start justify-between gap-4 mb-3">
                <div className="flex-1">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-slate-300 mb-1">
                    🌌 Celestial Prism Speed Overdrive
                  </div>
                  <p className="text-xs text-slate-400 leading-relaxed">
                    Controls the diamond-prism hue rotation velocity across all background star particles.
                    Slow drifts gently through the full spectrum; Overdrive fires hyper-frequency iridescent flash.
                  </p>
                </div>
              </div>
              {/* ── SPEC 1801: Master engine toggle — positioned above the 3-pill matrix ── */}
              <div className="flex items-center justify-between gap-4 mb-4 pb-3 border-b border-white/5">
                <div className="flex-1">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-slate-300 mb-1">
                    🌌 Celestial Particle Engines
                  </div>
                  <p className="text-xs text-slate-400 leading-relaxed">
                    Master power switch for all canvas particle animations — star field and lightning overlay.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleEngineToggle}
                  role="switch"
                  aria-checked={engineActive}
                  data-testid="profile-engine-toggle"
                  className="relative shrink-0 w-14 h-8 rounded-full transition-colors"
                  style={{
                    background: engineActive ? "rgba(99,102,241,0.28)" : "rgba(148,163,184,0.18)",
                    border: `1px solid ${engineActive ? "rgba(99,102,241,0.75)" : "rgba(148,163,184,0.35)"}`,
                    boxShadow: engineActive ? "0 0 14px rgba(99,102,241,0.55)" : undefined,
                  }}
                >
                  <span
                    className="absolute top-0.5 h-6 w-6 rounded-full transition-all"
                    style={{
                      left: engineActive ? "26px" : "3px",
                      background: engineActive
                        ? "radial-gradient(circle at 30% 30%, #a78bfa, #6366f1)"
                        : "radial-gradient(circle at 30% 30%, #cbd5e1, #64748b)",
                      boxShadow: engineActive ? "0 0 8px #6366f1" : "0 0 4px rgba(148,163,184,0.5)",
                    }}
                  />
                </button>
              </div>

              <div className="flex items-center gap-2" data-testid="profile-prism-speed-picker">
                {[
                  { key: "slow",      label: "Slow",      bg: "rgba(16,185,129,0.18)", border: "rgba(52,211,153,0.75)",  color: "#6ee7b7", glow: "rgba(52,211,153,0.35)"  },
                  { key: "normal",    label: "Normal",    bg: "rgba(6,182,212,0.18)",  border: "rgba(6,182,212,0.80)",   color: "#67e8f9", glow: "rgba(6,182,212,0.35)"   },
                  { key: "overdrive", label: "Overdrive", bg: "rgba(245,158,11,0.18)", border: "rgba(245,158,11,0.80)",  color: "#fcd34d", glow: "rgba(245,158,11,0.35)"  },
                ].map(({ key, label, bg, border, color, glow }) => {
                  const isActive = prismSpeed === key;
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => handlePrismSpeed(key)}
                      data-testid={`profile-prism-speed-${key}`}
                      aria-pressed={isActive}
                      className="px-3 h-7 rounded-full text-[10px] font-mono uppercase tracking-widest transition-all"
                      style={{
                        background: isActive ? bg    : "rgba(255,255,255,0.04)",
                        border:     `1px solid ${isActive ? border : "rgba(255,255,255,0.08)"}`,
                        color:      isActive ? color : "#64748b",
                        boxShadow:  isActive ? `0 0 10px ${glow}` : undefined,
                        transform:  isActive ? "scale(1.06)" : "scale(1)",
                      }}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>

              {/* ── SPEC 1601: Meteor Shower Intensity ──────────────────────── */}
              <div className="mt-4 pt-3 border-t border-white/5" data-testid="profile-meteor-intensity-block">
                <div
                  className="text-[10px] font-mono uppercase tracking-widest mb-1"
                  style={{ color: "#a5b4fc" }}
                >
                  Meteor Shower Intensity
                </div>
                <p className="text-xs text-slate-400 leading-relaxed mb-3">
                  Controls burst cadence on New Moon nights. Rare scatters a thin
                  stream every 25–40 s; Storm fires a Leonids-class outburst every
                  2–5 s.
                </p>
                <div className="flex items-center gap-2" data-testid="profile-meteor-intensity-picker">
                  {[
                    { key: "rare",   label: "Rare",   bg: "rgba(79,70,229,0.18)",  border: "rgba(99,102,241,0.75)",  color: "#a5b4fc", glow: "rgba(99,102,241,0.35)"  },
                    { key: "normal", label: "Normal", bg: "rgba(6,182,212,0.18)",  border: "rgba(6,182,212,0.80)",   color: "#67e8f9", glow: "rgba(6,182,212,0.35)"   },
                    { key: "storm",  label: "Storm",  bg: "rgba(139,92,246,0.18)", border: "rgba(167,139,250,0.80)", color: "#ddd6fe", glow: "rgba(167,139,250,0.35)" },
                  ].map(({ key, label, bg, border, color, glow }) => {
                    const isActive = meteorIntensity === key;
                    return (
                      <button
                        key={key}
                        type="button"
                        onClick={() => handleMeteorIntensity(key)}
                        data-testid={`profile-meteor-intensity-${key}`}
                        aria-pressed={isActive}
                        className="px-3 h-7 rounded-full text-[10px] font-mono uppercase tracking-widest transition-all"
                        style={{
                          background: isActive ? bg    : "rgba(255,255,255,0.04)",
                          border:     `1px solid ${isActive ? border : "rgba(255,255,255,0.08)"}`,
                          color:      isActive ? color : "#64748b",
                          boxShadow:  isActive ? `0 0 10px ${glow}` : undefined,
                          transform:  isActive ? "scale(1.06)" : "scale(1)",
                        }}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Master Save Configuration Changes button — sits at the base of
                the block and captures every dirty state in a single tap. */}
            <div className="pt-2 flex items-center justify-between gap-2">
              <span
                className="text-[9px] font-mono uppercase tracking-widest"
                data-testid="profile-config-dirty"
                style={{ color: dirty ? "#fcd34d" : "#64748b" }}
              >
                {dirty ? "● unsaved changes" : "○ all changes saved"}
              </span>
              <button
                type="button"
                onClick={saveConfig}
                disabled={cfgSaving || !dirty}
                data-testid="profile-config-save"
                className="h-9 px-4 rounded-md text-[10px] font-mono uppercase tracking-widest disabled:opacity-40"
                style={{
                  background: "rgba(6,182,212,0.22)",
                  border: "1px solid rgba(6,182,212,0.75)",
                  color: "#67e8f9",
                  boxShadow: dirty ? "0 0 14px rgba(6,182,212,0.55)" : undefined,
                }}
              >
                {cfgSaving ? "Saving…" : "💾 Save Configuration Changes"}
              </button>
            </div>
          </div>
        )}
        <ResetAuditLedgerStrip />
        <div className="mt-6">
          <MerchantLogoUploader />
        </div>
        <SubscriptionLifecycleGrid />
      </div>
    </DashboardLayout>
  );
}
